import { memo } from "react";
import { Transaction, TransactionCategory } from "../../types/budgethelper";
import { formatDateHeader, formatTime, getCategoryIcon } from "../utils/formatters";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';

interface TransactionListProps {
  groupedTransactions: Record<string, Transaction[]>;
  userProfileUserId: string;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transaction: Transaction) => void;
  getUserFriendlyName: (budgetGroup: string | undefined, userId: string) => string | null;
  getBudgetGroupName: (groupId: string) => string | null;
}

export const TransactionList = memo(({
  groupedTransactions,
  userProfileUserId,
  onEdit,
  onDelete,
  getUserFriendlyName,
  getBudgetGroupName
}: TransactionListProps) => {
  if (Object.keys(groupedTransactions).length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🛒</div>
        <p>No transactions yet</p>
      </div>
    );
  }

  return (
    <>
      {Object.entries(groupedTransactions).map(([date, txns]) => (
        <div key={date} className="transaction-group">
          <h3 className="date-header">{formatDateHeader(date)}</h3>
          {txns.map((transaction) => {
            // Calculate the logged-in user's split amount
            const userSplitAmount = transaction.splitAmounts?.[userProfileUserId] || 0;
            
            return (
            <div key={transaction.id} className="transaction-card">
              <div className="transaction-content">
                <div className="transaction-main">
                  <div className="transaction-left">
                    <div className={`category-icon ${transaction.category?.toLowerCase() || "other"}`}>
                      {getCategoryIcon(transaction.category as TransactionCategory)}
                    </div>
                    <span className="shop-name">{transaction.shopName}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span className="amount">${transaction.amount.toFixed(2)}</span>
                    {userSplitAmount > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '2px' }}>
                        You pay: ${userSplitAmount.toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="transaction-footer">
                  {transaction.budgetGroup && (
                    <span className="source-badge">
                      <GroupIcon style={{ fontSize: 14 }} /> {getBudgetGroupName(transaction.budgetGroup) || transaction.budgetGroup}
                    </span>
                  )}
                  {transaction.paidBy && (
                    <span className="source-text">
                      paid by {getUserFriendlyName(transaction.budgetGroup, transaction.paidBy) || transaction.paidBy}
                    </span>
                  )}
                  <span className="source-text">
                    from {getUserFriendlyName(transaction.budgetGroup, transaction.source) || transaction.source}
                  </span>
                  <span className="time">
                    {formatTime(transaction.timestamp)}
                  </span>
                  <div className="transaction-actions">
                    <button 
                      className="action-btn edit-btn" 
                      onClick={() => onEdit(transaction)}
                      title="Edit"
                    >
                      <EditIcon />
                    </button>
                    <button 
                      className="action-btn delete-btn" 
                      onClick={() => onDelete(transaction)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
          })}
        </div>
      ))}
    </>
  );
});
